import {
    getIrelandCodeLists,
    getUkCodeLists,
    getIeUnLocodeExtendedCodelist,
    getUkUnLocodeExtendedCodelist,
    getEuCodelist,
} from './client';
import { Codelists, CodelistsPayload, IrelandCodelistTypes, UkCodelistTypes, UnLocodeExtendedPayload } from './code';
import { camelCase } from 'lodash';
import { DeclarationTypes } from 'store/declarations/enums/common/declaration-types';
import { SelectOption } from 'components/ui/composed/declarations/formSelect/DeclarationSelect';
const AES_CODE_SUBSTRING = /cl\d{3}(Cl|NCl)/i;

export type TransformedCodelistPayload = {
    id: string;
    value: string;
    type: string;
    name: string;
    idServer: string;
    creatorName: string;
    data?: string;
};

export const transformCodelistPayload = (codelist: CodelistsPayload): TransformedCodelistPayload => {
    return {
        id: codelist.code,
        value: codelist.description,
        type: codelist.type,
        name: codelist.name,
        idServer: codelist.id,
        creatorName: codelist.creatorName,
        data: JSON.parse(codelist?.data ?? '{}'),
    };
};

const transformCodelistsForClient = (response: CodelistsPayload[]): IrelandCodelistTypes & UkCodelistTypes => {
    const codelistsObject: any = {};

    for (let i = 0; i < response.length; i++) {
        const codelistType = camelCase(response[i].type) as keyof IrelandCodelistTypes & UkCodelistTypes;
        let codelistName = camelCase(response[i].name) as keyof Codelists;

        if (AES_CODE_SUBSTRING.test(codelistName)) {
            const withRemovedSubstring = codelistName.replace(AES_CODE_SUBSTRING, '');
            codelistName = (withRemovedSubstring[0].toLowerCase() + withRemovedSubstring.slice(1)) as keyof Codelists;
        }

        if (codelistType && !codelistsObject[codelistType]) {
            codelistsObject[codelistType] = {};
        }

        if (codelistName && !codelistsObject[codelistType][codelistName]) {
            codelistsObject[codelistType][codelistName] = [];
        }

        const transformedPayload = transformCodelistPayload(response[i]);
        codelistsObject[codelistType][codelistName].push(transformedPayload);
    }

    return codelistsObject;
};

const transformUnLocodeExtendedCodelistForClient = (
    payload: UnLocodeExtendedPayload
): { list: SelectOption[]; pageNumber: number; total: number } => {
    const { list, pageNumber, total } = payload;

    const transformedData = list.map(transformCodelistPayload);

    return {
        list: transformedData,
        pageNumber,
        total,
    };
};

export const doGetIrelandCodelists: Function = (type: DeclarationTypes | undefined) => async (dispatch: Function) => {
    dispatch({ type: 'GET_IRELAND_CODELISTS_REQUEST' });

    try {
        const response = transformCodelistsForClient(await getIrelandCodeLists(type));

        dispatch({ type: 'GET_IRELAND_CODELISTS_SUCCESS', payload: response });
    } catch (e: any) {
        dispatch({ type: 'GET_IRELAND_CODELISTS_ERROR', payload: {} });
    }
};

export const doGetUkCodelists: Function = (type: DeclarationTypes | undefined) => async (dispatch: Function) => {
    dispatch({ type: 'GET_UK_CODELISTS_REQUEST' });

    try {
        const response = transformCodelistsForClient(await getUkCodeLists(type));

        dispatch({ type: 'GET_UK_CODELISTS_SUCCESS', payload: response });
    } catch (e: any) {
        dispatch({ type: 'GET_UK_CODELISTS_ERROR', payload: {} });
    }
};

export const doGetIeUnLocodeExtendedCodelist: Function =
    (customerId: string | undefined, pagination: { page: number; size: number }) => async (dispatch: Function) => {
        dispatch({ type: 'GET_IRELAND_CODELISTS_REQUEST' });

        try {
            const response = await getIeUnLocodeExtendedCodelist(customerId, pagination);
            const transformedData = transformUnLocodeExtendedCodelistForClient(response);

            dispatch({ type: 'GET_IRELAND_UNLOCODEEXTENDED_CODELISTS_SUCCESS', payload: transformedData });
        } catch (e: any) {
            dispatch({ type: 'GET_IRELAND_CODELISTS_ERROR', payload: {} });
        }
    };

export const doGetUkUnLocodeExtendedCodelist: Function =
    (customerId: string | undefined, pagination: { page: number; size: number }) => async (dispatch: Function) => {
        dispatch({ type: 'GET_UK_CODELISTS_REQUEST' });

        try {
            const response = await getUkUnLocodeExtendedCodelist(customerId, pagination);
            const transformedData = transformUnLocodeExtendedCodelistForClient(response);

            dispatch({ type: 'GET_UK_UNLOCODEEXTENDED_CODELISTS_SUCCESS', payload: transformedData });
        } catch (e: any) {
            dispatch({ type: 'GET_UK_CODELISTS_ERROR', payload: {} });
        }
    };

export const doGetICS2Codelist: Function =
    (codelistName: string | undefined) =>
    async (dispatch: Function): Promise<SelectOption[]> => {
        if (!codelistName) {
            throw new Error('Missing codelist name');
        }

        const response = await getEuCodelist(DeclarationTypes.ICS2, codelistName);
        const values = (response.euCodeLists ?? []).map((code) => ({
            id: code.code,
            value: code.description,
        }));

        dispatch({
            type: 'GET_ICS2_CODELIST_SUCCESS',
            payload: {
                codelistName,
                values,
            },
        });

        return values;
    };
